// nodeStream credits: 1 credit = 0.01 of the ledger's unit = 10^6 tinybars, with no exchange rate, so a balance in credits
// never moves with the market price.
export const TINYBARS_PER_CREDIT_DIGITS = 6;

const INTEGER = /^-?\d+$/;

/**
 * A tinybar amount as an exact decimal string of credits (tinybars ÷ 10^6), without going through a float. Mirror node
 * balances are integers that can exceed `Number.MAX_SAFE_INTEGER`, so they are taken as strings (or bigints) too.
 * @throws when the value is not a whole number of tinybars
 */
export function tinybarsToCreditsString(tinybars: string | number | bigint): string {
  const text = typeof tinybars === 'string' ? tinybars.trim() : String(tinybars);
  if (typeof tinybars === 'number' && !Number.isSafeInteger(tinybars)) {
    throw new Error(`Not a whole number of tinybars: ${text}`);
  }
  if (!INTEGER.test(text)) throw new Error(`Not a whole number of tinybars: ${text}`);

  const negative = text.startsWith('-');
  const digits = (negative ? text.slice(1) : text).replace(/^0+(?=\d)/, '');
  const padded = digits.padStart(TINYBARS_PER_CREDIT_DIGITS + 1, '0');
  const whole = padded.slice(0, -TINYBARS_PER_CREDIT_DIGITS);
  const fraction = padded.slice(-TINYBARS_PER_CREDIT_DIGITS).replace(/0+$/, '');
  const value = fraction ? `${whole}.${fraction}` : whole;
  return negative && value !== '0' ? `-${value}` : value;
}

/**
 * A tinybar amount in credits (tinybars ÷ 10^6). The division is exact; only the final conversion to a number rounds.
 * @throws when the value is not a whole number of tinybars
 */
export function tinybarsToCredits(tinybars: string | number | bigint): number {
  return Number(tinybarsToCreditsString(tinybars));
}

/**
 * A credit amount for people to read, grouped (`12,345`), with at most `maxFractionDigits` decimals. It is truncated,
 * never rounded up, so a balance is never shown as more than it is.
 */
export function formatCredits(credits: number, maxFractionDigits = 0): string {
  if (!Number.isFinite(credits)) return '–';
  const factor = 10 ** maxFractionDigits;
  // toFixed first: 1.15 * 100 is 114.99999999999999 as a float
  const truncated = Math.trunc(Number((credits * factor).toFixed(6))) / factor;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(truncated === 0 ? 0 : truncated);
}

/**
 * A credit amount typed by a person, as `ns-credits-input` reads it: digits, with grouping commas and spaces allowed. Null
 * when the text is empty or isn't a whole, safe number of credits (a fraction is refused, not rounded).
 */
export function parseCreditsAmount(input: string | null | undefined): number | null {
  const text = (input ?? '').replace(/[,\s]/g, '');
  if (!/^\d+$/.test(text)) return null;
  const credits = Number(text);
  return Number.isSafeInteger(credits) ? credits : null;
}

// A stored balance reading's balance in credits, or null when it has none (or an unreadable one)
export function readingCredits(reading: { tinybars?: string | null } | null | undefined): number | null {
  if (!reading?.tinybars) return null;
  try {
    return tinybarsToCredits(reading.tinybars);
  } catch {
    return null;
  }
}
